import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import * as React from "react";
import { cx } from "../internal/cx.js";
import { rendersNativeButton } from "../internal/nativeButton.js";
import { useControllableState } from "./hooks.js";
import { CloseIcon } from "./icons.js";
import { DialogRoot } from "../vendor/@base-ui/react/dialog/root/DialogRoot.js";
import { DialogTrigger } from "../vendor/@base-ui/react/dialog/trigger/DialogTrigger.js";
import { DialogPortal } from "../vendor/@base-ui/react/dialog/portal/DialogPortal.js";
import { DialogBackdrop } from "../vendor/@base-ui/react/dialog/backdrop/DialogBackdrop.js";
import { DialogPopup } from "../vendor/@base-ui/react/dialog/popup/DialogPopup.js";
import { DialogTitle } from "../vendor/@base-ui/react/dialog/title/DialogTitle.js";
import { DialogClose } from "../vendor/@base-ui/react/dialog/close/DialogClose.js";
import { DialogDescription } from "../vendor/@base-ui/react/dialog/description/DialogDescription.js";
const AppLauncher = React.forwardRef(function AppLauncher2({
  apps,
  current,
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  trigger,
  title = "Switch workspace",
  description,
  currentLabel = "You are here",
  privateNetworkLabel = "Private network",
  closeLabel = "Close",
  emptyLabel = "No apps are available to you.",
  className,
  ...rest
}, ref) {
  const [open, setOpen] = useControllableState(openProp, defaultOpen, onOpenChange);
  const listRef = React.useRef(null);
  const initialFocus = React.useCallback((type) => {
    if (type === "touch") return true;
    const list = listRef.current;
    const target = list?.querySelector('a[aria-current="true"]') ?? list?.querySelector("a[href]");
    return target ?? true;
  }, []);
  return /* @__PURE__ */ jsxs(DialogRoot, { open, onOpenChange: (next) => setOpen(next), children: [
    trigger && /* @__PURE__ */ jsx(DialogTrigger, { render: trigger, nativeButton: rendersNativeButton(trigger) }),
    /* @__PURE__ */ jsxs(DialogPortal, { children: [
      /* @__PURE__ */ jsx(DialogBackdrop, { className: "ev-scrim" }),
      /* @__PURE__ */ jsxs(
        DialogPopup,
        {
          ref,
          className: cx("ev-dialog", "ev-dialog--wide", "ev-launcher-dialog", "ev-root", className),
          initialFocus,
          ...rest,
          children: [
            /* @__PURE__ */ jsxs("div", { className: "ev-dialog__head", children: [
              /* @__PURE__ */ jsx(DialogTitle, { children: title }),
              /* @__PURE__ */ jsx(DialogClose, { className: "ev-btn ev-btn--ghost ev-btn--icon", "aria-label": closeLabel, children: /* @__PURE__ */ jsx(CloseIcon, {}) })
            ] }),
            /* @__PURE__ */ jsxs("div", { className: "ev-dialog__body", children: [
              description != null && /* @__PURE__ */ jsx(DialogDescription, { className: "ev-muted ev-launcher__description", children: description }),
              apps.length === 0 ? /* @__PURE__ */ jsx("div", { className: "ev-empty", children: emptyLabel }) : /* @__PURE__ */ jsx("ul", { ref: listRef, className: "ev-launcher", role: "list", children: apps.map((app) => /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(
                LauncherTile,
                {
                  app,
                  isCurrent: app.id === current,
                  currentLabel,
                  privateNetworkLabel
                }
              ) }, app.id)) })
            ] })
          ]
        }
      )
    ] })
  ] });
});
function LauncherTile({
  app,
  isCurrent,
  currentLabel,
  privateNetworkLabel
}) {
  const url = app.url || null;
  const note = isCurrent ? currentLabel : url ? null : privateNetworkLabel;
  const content = /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("span", { className: "ev-app-tile", "aria-hidden": "true", children: app.short }),
    /* @__PURE__ */ jsxs("span", { className: "ev-launcher__text", children: [
      /* @__PURE__ */ jsx("b", { children: app.name }),
      app.description && /* @__PURE__ */ jsx("small", { children: app.description })
    ] }),
    note && /* @__PURE__ */ jsx("span", { className: "ev-launcher__ext", children: note })
  ] });
  const common = {
    className: "ev-launcher__app",
    "data-app": app.id,
    "aria-current": isCurrent ? "true" : void 0
  };
  if (url) {
    return /* @__PURE__ */ jsx("a", { ...common, href: url, children: content });
  }
  return /* @__PURE__ */ jsx("div", { ...common, "data-private": "true", children: content });
}
export {
  AppLauncher
};
//# sourceMappingURL=AppLauncher.js.map
